/**
 * Material aus dem Angebot übernehmen — Positionen der Angebote, je mit
 * Materialvorschlag aus dem Stamm; übernommen wird nur, was angehakt ist.
 * Aus ProjektMaterial.tsx herausgelöst (02.10.2026).
 *
 * „Übernehmen und anfragen" (03.10.2026, bedarfsorientiert): die eben übernommenen
 * Materialien gehen gleich ins Anfrage-Fenster — dort wird bestimmt, bei wem.
 */
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { fehlerText } from "@vimaana/ui";

import {
  useAngebotPositionen, useProjektMaterialAendern, type AngebotPosition, type UebernahmeEintrag,
} from "../../hooks/useLieferanten";
import MaterialWahl, { type GewaehltesMaterial } from "../lieferanten/MaterialWahl";
import "../../styles/lieferanten.css";

type Wahl = { an: boolean; material: GewaehltesMaterial | null };

/** Titel, die nach Arbeit klingen — der Vorschlag bleibt sichtbar, angehakt wird er nicht. */
const ARBEIT = /\b(einbau|einbauen|montage|montieren|verlegen|verlegung|arbeit|wartung|entsorg\w*|pflege|schnitt|anfahrt|aushub|ausheben|pflanzen der|erstellen|abbau|aufbau)\b/i;

export default function AngebotUebernahme({ projektId, onClose, onAnfragen }: {
  projektId: string; onClose: () => void;
  /** Gesetzt: zweiter Knopf „Übernehmen und anfragen" — bekommt die übernommenen Materialien. */
  onAnfragen?: (pmIds: string[]) => void;
}) {
  const pos = useAngebotPositionen(projektId, true);
  const a = useProjektMaterialAendern(projektId);
  const [wahl, setWahl] = useState<Record<string, Wahl>>({});
  const [aendern, setAendern] = useState<string | null>(null);

  const schluessel = (angebotId: string, p: AngebotPosition) => `${angebotId}|${p.key}`;
  // Vorangehakt wird nur, was nach Material aussieht: nicht die Arbeitsposition
  // zum selben Material („Einbau Wasserbausteine von Hand" neben „Wasserbausteine
  // liefern") — je Material nur die erste Position.
  const erste = useMemo(() => {
    const gesehen = new Set<string>();
    const ja = new Set<string>();
    for (const ang of pos.data?.angebote ?? []) {
      for (const p of ang.positionen) {
        if (!p.vorschlag || p.uebernommen || p.schon_in_liste || ARBEIT.test(p.titel) || gesehen.has(p.vorschlag.id)) continue;
        gesehen.add(p.vorschlag.id);
        ja.add(`${ang.id}|${p.key}`);
      }
    }
    return ja;
  }, [pos.data]);
  const stand = (angebotId: string, p: AngebotPosition): Wahl => wahl[schluessel(angebotId, p)] ?? {
    an: erste.has(schluessel(angebotId, p)),
    material: p.vorschlag ? { id: p.vorschlag.id, name: p.vorschlag.name, einheit: p.vorschlag.einheit, art: p.vorschlag.art } : null,
  };
  const setzen = (angebotId: string, p: AngebotPosition, w: Partial<Wahl>) =>
    setWahl((v) => ({ ...v, [schluessel(angebotId, p)]: { ...stand(angebotId, p), ...w } }));

  const eintraege: UebernahmeEintrag[] = [];
  for (const ang of pos.data?.angebote ?? []) {
    for (const p of ang.positionen) {
      const w = stand(ang.id, p);
      if (w.an && w.material && !p.uebernommen) {
        eintraege.push({ material_id: w.material.id, menge: p.menge, einheit: p.einheit || w.material.einheit, angebot_id: ang.id, position_key: p.key });
      }
    }
  }

  return createPortal(
    <div className="vg-overlay lf-uebernahme" role="dialog" aria-modal="true" aria-label="Material aus dem Angebot übernehmen">
      <button type="button" className="vg-overlay-backdrop" aria-label="Schließen" onClick={onClose} />
      <div className="vg-overlay-panel lf-uebernahme-panel">
        <div className="vg-overlay-head lf-uebernahme-kopf">
          <h2 className="vg-overlay-title">Material aus dem Angebot</h2>
          <button type="button" className="vg-overlay-close k-leeds-zu" aria-label="Schließen" onClick={onClose}>×</button>
        </div>
        <p className="lf-hinweis">Angehakt sind die Positionen, zu denen es ein passendes Material im Katalog gibt. Arbeitsleistungen einfach auslassen; „Material/Maschine …“ tippen, um etwas anderes zu wählen oder es neu in den Katalog aufzunehmen. Den Lieferanten wählst du danach je Zeile — auf Wunsch als Standard.</p>
        <div className="lf-uebernahme-inhalt">
          {pos.isPending && <p className="lf-leer">Lade Angebote …</p>}
          {pos.isError && <p className="lf-fehler">{fehlerText(pos.error)}</p>}
          {pos.data && pos.data.angebote.length === 0 && <p className="lf-leer">Zu diesem Projekt gibt es kein Angebot mit Positionen.</p>}
          {pos.data?.angebote.map((ang) => (
            <section key={ang.id} className="lf-angebot">
              <h3>{ang.art === "entwurf" ? "Angebotsentwurf" : `Angebot ${ang.nummer}`}{ang.titel ? ` · ${ang.titel}` : ""}{ang.status && ang.art !== "entwurf" ? ` · ${ang.status}` : ""}</h3>
              <ul className="lf-liste">
                {ang.positionen.map((p) => {
                  const w = stand(ang.id, p);
                  const k = schluessel(ang.id, p);
                  return (
                    <li key={k} className={"lf-zeile lf-pzeile" + (p.uebernommen ? " ist-alt" : "")}>
                      <label className="lf-pzeile-haken">
                        <input type="checkbox" checked={p.uebernommen || (w.an && !!w.material)} disabled={p.uebernommen}
                          onChange={(e) => { if (e.target.checked && !w.material) setAendern(k); setzen(ang.id, p, { an: e.target.checked }); }} />
                        <span className="lf-zeile-haupt">
                          <span className="lf-zeile-name">{p.titel}{p.optional ? <em className="lf-optional"> optional</em> : null}</span>
                          <span className="lf-zeile-info">{[[p.menge, p.einheit].filter(Boolean).join(" "), p.uebernommen ? "schon übernommen" : p.schon_in_liste ? "Material steht schon in der Liste" : ""].filter(Boolean).join(" · ")}</span>
                        </span>
                      </label>
                      {!p.uebernommen && (
                        <button type="button" className={"lf-knopf lf-pzeile-material" + (w.material ? "" : " ist-leer")} onClick={() => setAendern(aendern === k ? null : k)}>
                          {w.material ? `→ ${w.material.name}${w.material.art === "maschine" ? " (Maschine)" : ""}` : "Material/Maschine …"}
                        </button>
                      )}
                      {aendern === k && (
                        <div className="lf-zeile-unter">
                          <MaterialWahl start={p.titel} onAbbruch={() => setAendern(null)}
                            onWahl={(m) => { setzen(ang.id, p, { material: m, an: true }); setAendern(null); }} />
                          {w.material && (
                            <button type="button" className="lf-knopf" onClick={() => { setzen(ang.id, p, { material: null, an: false }); setAendern(null); }}>
                              Kein Material (Arbeitsleistung)
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
        {a.uebernehmen.isError && <div className="lf-fehler">{fehlerText(a.uebernehmen.error)}</div>}
        <div className="lf-knoepfe lf-uebernahme-fuss">
          <button type="button" className="lf-knopf" onClick={onClose}>Abbrechen</button>
          <button type="button" className={"lf-knopf" + (onAnfragen ? "" : " is-primaer")} disabled={eintraege.length === 0 || a.uebernehmen.isPending}
            onClick={() => a.uebernehmen.mutate(eintraege, { onSuccess: onClose })}>
            {a.uebernehmen.isPending ? "Übernimmt …" : `${eintraege.length} übernehmen`}
          </button>
          {onAnfragen && (
            <button type="button" className="lf-knopf is-primaer" disabled={eintraege.length === 0 || a.uebernehmen.isPending}
              title="Übernimmt die Positionen und öffnet die Anfrage — dort wählst du, bei welchem Lieferanten"
              onClick={() => a.uebernehmen.mutate(eintraege, {
                onSuccess: (d) => {
                  const ids = d.materialien
                    .filter((m) => eintraege.some((e) => e.angebot_id === m.angebot_id && e.position_key === m.position_key))
                    .map((m) => m.id);
                  onClose();
                  onAnfragen(ids);
                },
              })}>
              {eintraege.length} übernehmen und anfragen
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
